import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaBolt, FaWrench, FaTint, FaIndustry, FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CustomButton from "../../Uicomponents/Button";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Content slides with service data and corresponding background images
  const slides = [
    {
      title: "AKTP",
      subtitle: "Your Trusted Partner in Industrial Excellence",
      description: "Leading supplier of electrical, hardware, plumbing, and explosion-proof materials with over two decades of expertise.",
      icon: FaIndustry,
      features: ["20+ Years Experience", "5000+ Products", "Expert Consultation", "Quality Assurance"],
      buttonText: "Discover Our Services",
      color: "from-blue-600 to-purple-600",
      backgroundImage: "/images/warehouse.webp",
      badge: "TRUSTED INDUSTRIAL SUPPLIER SINCE 2004"
    },
    {
      title: "One-Stop Industrial Shop",
      subtitle: "Everything You Need Under One Roof",
      description: "From electrical components to explosion-proof equipment, we provide comprehensive solutions for all your industrial requirements.",
      icon: FaBolt,
      features: ["Complete Range", "Bulk Orders", "Fast Delivery", "Working Hours Support"],
      buttonText: "View Our Catalog",
      color: "from-green-600 to-teal-600",
      backgroundImage: "/images/electrician.webp",
      badge: "COMPREHENSIVE INDUSTRIAL SOLUTIONS"
    },
    {
      title: "Quality Guaranteed",
      subtitle: "Premium Materials, Proven Performance",
      description: "All our products meet international standards and come with manufacturer warranties. Your project's success is our priority.",
      icon: FaWrench,
      features: ["ISO Certified", "Warranty Included", "Quality Testing", "Brand Authorized"],
      buttonText: "Quality Standards",
      color: "from-orange-600 to-red-600",
      backgroundImage: "/images/electrical_quality.webp",
      badge: "QUALITY CERTIFIED PRODUCTS ONLY"
    },
    {
      title: "Expert Consultation",
      subtitle: "Professional Guidance for Every Project",
      description: "Our experienced team provides technical consultation and custom solutions tailored to your specific industrial needs during working hours.",
      icon: FaTint,
      features: ["Business Hours Consultation", "Custom Solutions", "Project Planning", "Installation Support"],
      buttonText: "Get Expert Advice",
      color: "from-purple-600 to-pink-600",
      backgroundImage: "/images/electrical_consultation.webp",
      badge: "PROFESSIONAL CONSULTATION AVAILABLE"
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Animation variants
  const slideVariants = {
    enter: { opacity: 0, x: 100 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 }
  };

  const backgroundVariants = {
    enter: { opacity: 0, scale: 1.1 },
    center: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 }
  };

  const featureVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1 + 0.5,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };

  const floatingAnimation = {
    y: [-10, 10, -10],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const navigate = useNavigate();

  // Add navigation logic functions
  const getNavigationPath = (slideIndex) => {
    const navigationPaths = {
      0: "/service", // AKTP -> Services
      1: "/products", // One-Stop Industrial Shop -> Products
      2: "/about", // Quality Guaranteed -> About (quality standards)
      3: "/contact" // Expert Consultation -> Contact
    };
    return navigationPaths[slideIndex] || "/service";
  };

  const getSecondaryNavigationPath = (slideIndex) => {
    const secondaryPaths = {
      0: "/about", // AKTP -> Learn More about company
      1: "/service", // One-Stop Shop -> View services
      2: "/products", // Quality -> View certified products
      3: "/service" // Expert Consultation -> View consultation services
    };
    return secondaryPaths[slideIndex] || "/about";
  };

  // Handle primary button click
  const handlePrimaryButtonClick = () => {
    navigate(getNavigationPath(currentSlide));
  };

  // Handle secondary button click (Learn More)
  const handleSecondaryButtonClick = () => {
    navigate(getSecondaryNavigationPath(currentSlide));
  };

  // Handle scroll to next section
  const handleScrollToNext = () => {
    const nextSection = document.querySelector('#why-choose-us');
    console.log("Next Section:", nextSection);
    if (nextSection) {
      nextSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Fallback: scroll down by viewport height
      window.scrollTo({
        top: window.innerHeight * 0.9,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="p-0 md:p-8">
      <section className="relative w-full flex items-center justify-center overflow-hidden md:rounded-3xl">
        {/* Animated Background Images */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            variants={backgroundVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 1.2, ease: "easeInOut" }}
            className="absolute inset-0 md:rounded-3xl overflow-hidden"
          >
            <img
              src={currentSlideData.backgroundImage}
              alt={currentSlideData.title}
              className="w-full h-full object-cover"
            />
            {/* Linear gradient only till 50% */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/95 to-transparent" style={{ background: `linear-gradient(to right, #0E324C 0%, #0E324C 35%, rgba(14, 50, 76, 0.8) 50%, transparent 100%)` }}></div>
          </motion.div>
        </AnimatePresence>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden md:rounded-3xl">
          <motion.div
            animate={floatingAnimation}
            className="absolute top-10 md:top-20 left-4 md:left-10 w-20 h-20 md:w-32 md:h-32 bg-secondary/15 rounded-full blur-2xl"
          />
          <motion.div
            animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1 } }}
            className="absolute bottom-20 md:bottom-32 left-1/4 w-16 h-16 md:w-24 md:h-24 bg-secondary/10 rounded-full blur-xl"
          />
          <motion.div
            animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 2 } }}
            className="absolute top-1/3 left-8 md:left-20 w-12 h-12 md:w-16 md:h-16 bg-secondary/20 rounded-full blur-lg"
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 flex items-center min-h-[60vh] md:min-h-[90vh] lg:min-h-[85vh] py-8 md:py-0">
          {/* Content Container with padding to avoid overlap */}
          <div className="w-full max-w-2xl md:pr-32 lg:pr-40">
            <div className="flex flex-col justify-center space-y-3 md:space-y-6 py-4 md:pb-20">
              {/* Animated Badge */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: -20, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.8 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 bg-secondary px-3 md:px-4 py-1.5 md:py-2 rounded-md mb-3 md:mb-6 flex-shrink-0"
                >
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span className="text-primary font-bold text-xs uppercase tracking-wider">
                    {currentSlideData.badge}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Animated Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                  className="space-y-3 md:space-y-6"
                >
                  {/* Main Title */}
                  <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white leading-tight"
                  >
                    {currentSlideData.title.split(' ').map((word, index) => (
                      <span key={index} className={index === currentSlideData.title.split(' ').length - 1 ? 'block' : ''}>
                        {word}{' '}
                      </span>
                    ))}
                  </motion.h1>

                  {/* Subtitle */}
                  <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="text-base sm:text-xl lg:text-2xl text-secondary font-semibold"
                  >
                    {currentSlideData.subtitle}
                  </motion.h2>

                  {/* Description - Hidden on Mobile */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="hidden md:block text-base md:text-lg text-gray-200 leading-relaxed"
                  >
                    {currentSlideData.description}
                  </motion.p>

                  {/* Features Grid - Simplified for Mobile */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="grid grid-cols-2 md:grid-cols-2 gap-2 md:gap-4 pt-2 md:pt-4"
                  >
                    {currentSlideData.features.slice(0, 2).map((feature, index) => (
                      <motion.div
                        key={feature}
                        custom={index}
                        variants={featureVariants}
                        initial="hidden"
                        animate="visible"
                        className="flex items-center gap-2 text-gray-200"
                      >
                        <div className="w-2 h-2 bg-secondary rounded-full flex-shrink-0"></div>
                        <span className="text-xs md:text-sm font-medium">{feature}</span>
                      </motion.div>
                    ))}
                    {/* Show remaining features only on desktop */}
                    <div className="hidden md:contents">
                      {currentSlideData.features.slice(2).map((feature, index) => (
                        <motion.div
                          key={feature}
                          custom={index + 2}
                          variants={featureVariants}
                          initial="hidden"
                          animate="visible"
                          className="flex items-center gap-2 text-gray-200"
                        >
                          <div className="w-2 h-2 bg-secondary rounded-full flex-shrink-0"></div>
                          <span className="text-sm font-medium">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Action Buttons - Improved Layout */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="pt-3 md:pt-6 flex-shrink-0"
                  >
                    <div className="flex flex-col md:flex-row gap-3 md:gap-4">
                      {/* Primary Button */}
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: "0 15px 35px rgba(254, 208, 41, 0.4)" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handlePrimaryButtonClick}
                        className="w-full md:flex-1 md:max-w-xs bg-secondary text-primary px-6 md:px-8 py-3 md:py-4 text-sm md:text-base font-bold rounded-lg hover:bg-yellow-400 transition-all duration-300 flex items-center gap-2 md:gap-3 justify-center shadow-xl"
                      >
                        <IconComponent />
                        <span className="truncate">{currentSlideData.buttonText}</span>
                        <FaArrowRight />
                      </motion.button>

                      {/* Secondary Button - Desktop */}
                      <motion.button
                        whileHover={{
                          scale: 1.05,
                          backgroundColor: "rgba(255, 255, 255, 0.15)"
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleSecondaryButtonClick}
                        className="hidden md:flex items-center gap-3 border-2 border-white/50 text-white px-8 py-4 text-base font-bold rounded-lg hover:border-secondary transition-all duration-300 backdrop-blur-sm"
                      >
                        Learn More
                      </motion.button>
                    </div>
                  </motion.div>
                </motion.div>
              </AnimatePresence>

              {/* Business Hours Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="pt-4 border-t border-white/20"
              >
                <div className="text-gray-300 text-sm">
                  <p className="font-semibold text-secondary mb-1">Business Hours:</p>
                  <p>Saturday - Thursday: 8:00 AM - 1:00 PM, 4:00 PM - 8:30 PM</p>
                  <p>Friday: Closed</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Slide Navigation - Desktop */}
          <div className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevSlide}
              className="w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white border border-white/20 transition-all duration-300"
            >
              <FaChevronLeft />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextSlide}
              className="w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white border border-white/20 transition-all duration-300"
            >
              <FaChevronRight />
            </motion.button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide
                ? 'bg-secondary shadow-lg shadow-secondary/50'
                : 'bg-white/30 hover:bg-white/50'
                }`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-4 md:bottom-6 left-4 md:left-12"
          onClick={handleScrollToNext} // Add this line
          style={{ cursor: "pointer", zIndex: 1000 }} // Ensure it's above other elements
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-white/70 text-xs md:text-sm"
          >
            Scroll to explore ↓
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default HeroSection;